import React, { useEffect, useState } from 'react';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import colors from 'src/styles/colors.module.scss';
import styles from './budgetAccounts.module.scss';
import { useAccountSyncStatuses, useAllAccounts } from 'src/hooks/queries/accounts';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import adobeTags from 'src/types/adobe-tags';
import ExpandableText from 'src/components/atomic/molecules/ExpandableText/ExpandableText';
import SubsectionHeader from 'src/components/atomic/atoms/subsectionHeader/SubsectionHeader';
import {
    AMEX_PRIVACY_CENTER,
    DATA_CONNECTION_TERMS_AND_CONDITIONS,
    SPENDSMART_TERMS_AND_CONDITIONS
} from 'src/utils/constants';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import accountStyles from 'src/components/atomic/atoms/accountsList/accountsList.module.scss';
import { getSyncingInstitutions, sortAccountsAlphabetically } from 'src/utils/accounts.utils';
import ToggleAccountRow from 'src/components/atomic/atoms/toggleAccountRow/ToggleAccountRow';
import AccountService from 'src/services/account.service';
import FormatService from 'src/services/format.service';
import { uniq } from 'lodash';

function BudgetAccounts(): JSX.Element {
    const { isLoading: isLoadingAccounts, data: accounts } = useAllAccounts();
    const { data: syncStatuses } = useAccountSyncStatuses();
    if (isLoadingAccounts) return <Loading />;
    const [syncingInstitutions, setSyncingInstitutions] = useState<string[]>(
        getSyncingInstitutions(syncStatuses, accounts)
    );

    useEffect(() => {
        setSyncingInstitutions(getSyncingInstitutions(syncStatuses, accounts));
    }, [syncStatuses]);

    const handleToggleClick = async (institutionId: string): Promise<void> => {
        setSyncingInstitutions(uniq([...syncingInstitutions, institutionId]));
    };

    return (
        <div className={styles.container}>
            <SubsectionHeader
                title={LiteralsService.getLiteral('Budget.Connect.spendingAccounts')}
                body={LiteralsService.getLiteral('Budget.Connect.wellUseYour')}
            />
            <div className={accountStyles.accountsList}>
                <Typography variant={TypographyVariants.SUBTEXT} component="p" className={colors.textWeak}>
                    {LiteralsService.getLiteral('Budget.Connect.selectAccountsToInclude')}
                </Typography>
                {accounts &&
                    sortAccountsAlphabetically(accounts).map((account) => (
                        <ToggleAccountRow
                            key={`account-row-${account.accountId}`}
                            title={AccountService.getDisplayName(account)}
                            subtitle={account.institution?.name}
                            institution={account.institution}
                            balance={FormatService.format(account.current)}
                            hidden={account.hidden}
                            id={account.id}
                            accountId={account.accountId}
                            isSyncing={
                                AccountService.isSyncingAccount(account.accountId, syncStatuses) ||
                                AccountService.hasSameInstitutionAsSyncingAccount(
                                    account.institutionId,
                                    accounts,
                                    syncStatuses
                                ) ||
                                syncingInstitutions.includes(account.institution?.institutionId)
                            }
                            error={AccountService.getAccountSyncError(account.accountId, syncStatuses)}
                            showBalance={false}
                            onClick={async () => await handleToggleClick(account.institution?.institutionId)}
                        />
                    ))}
            </div>
            <ExpandableText
                className={styles.collapsible}
                title={LiteralsService.getLiteral('Budget.Connect.pendingCharges.title')}
                body={
                    <>
                        {LiteralsService.getLiteral('Budget.Connect.pendingCharges.content')}
                        <Anchor href={SPENDSMART_TERMS_AND_CONDITIONS} tag={adobeTags.Reconnect.TC} inline>
                            {LiteralsService.getLiteral('Budget.termsAndConditions')}
                        </Anchor>
                        {LiteralsService.getLiteral('Budget.theAmex')}
                        <Anchor href={AMEX_PRIVACY_CENTER} tag={adobeTags.Reconnect.PrivacyCenter} inline>
                            {LiteralsService.getLiteral('Budget.privacyCenter')}
                        </Anchor>
                        {LiteralsService.getLiteral('Budget.andThe')}
                        <Anchor
                            href={DATA_CONNECTION_TERMS_AND_CONDITIONS}
                            tag={adobeTags.Reconnect.DataConnectionTerms}
                            inline
                        >
                            {LiteralsService.getLiteral('Budget.dataConnectionTermsAndConditionsLink')}
                        </Anchor>
                        .
                    </>
                }
                tagName={adobeTags.AccountConnection}
                collapsibleContentClassName={styles.collapsibleContent}
            />
        </div>
    );
}

export default BudgetAccounts;
